import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { buildApiUrl } from '../../config/api';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LocalSpinner = styled.div<{ $size?: number }>`
  width: ${props => props.$size || 24}px;
  height: ${props => props.$size || 24}px;
  border: 3px solid rgba(93, 114, 44, 0.2);
  border-top-color: #5D722C;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const CenterLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  width: 100%;
  color: #5D722C;
  font-size: 13px;
  font-weight: 500;
  gap: 12px;
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 10;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 10px 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 0 0 10px;
  color: #22334d;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  flex-wrap: wrap;
  user-select: none;
`;

const SectionLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #34495e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActionButton = styled.button`
  padding: 7px 10px;
  border: 1.5px solid #b8cc8a;
  border-radius: 6px;
  background: #eaf4d6;
  color: #2f4a1c;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  transition: all 0.12s;

  &:hover {
    background: #dcebbd;
    border-color: #9fbc69;
  }
`;

const Card = styled.div<{ $isLoading?: boolean }>`
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  cursor: ${props => (props.$isLoading ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$isLoading ? 0.7 : 1)};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;

  &:hover {
    border-color: #5D722C;
    background: white;
    transform: translateY(-2px);
  }
`;

const ThumbWrap = styled.div`
  width: 100%;
  height: 120px;
  border-radius: 4px;
  background: transparent;
  position: relative;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  height: 120px;
  border-radius: 4px;
  object-fit: contain;
`;

const Name = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  text-align: center;
`;

interface EsqModal {
  id: number;
  name: string;
  image_path: string;
  thumb_path?: string;
  base_path?: string;
  category: string;
  vase_path?: string;
}

interface EsqModalsProps {
  onSelect: (modal: EsqModal) => void;
  colorId: number | null;
  onClose?: () => void;
}

const EsqModals: React.FC<EsqModalsProps> = ({ onSelect, colorId, onClose }) => {
  const [modals, setModals] = useState<EsqModal[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageStatus, setImageStatus] = useState<Record<number, 'loading' | 'loaded' | 'error'>>({});

  const setThumbStatus = (id: number, status: 'loading' | 'loaded' | 'error') => {
    setImageStatus(prev => (prev[id] === status ? prev : { ...prev, [id]: status }));
  };

  useEffect(() => {
    let url = buildApiUrl('/api/esq/modals');
    if (colorId) {
      url += `?color_id=${colorId}`;
    }

    fetch(url)
      .then(res => res.json())
      .then(data => {
        const nextModals = Array.isArray(data) ? data : [];
        setModals(nextModals);
        const nextStatus: Record<number, 'loading' | 'loaded' | 'error'> = {};
        nextModals.forEach((m: EsqModal) => {
          nextStatus[m.id] = 'loading';
        });
        setImageStatus(nextStatus);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [colorId]);

  if (loading) return (
    <CenterLoader>
      <LocalSpinner $size={32} />
      <span>Loading models...</span>
    </CenterLoader>
  );

  return (
    <div style={{ position: 'relative' }}>
      <Breadcrumbs>
        <span className="current" style={{ fontWeight: 600 }}>Select Model</span>
      </Breadcrumbs>

      <SectionLabel>Select Model</SectionLabel>

      <Grid>
        {modals.map(modal => (
          <Card 
            key={modal.id} 
            $isLoading={(imageStatus[modal.id] || 'loading') === 'loading'}
            onClick={() => {
              if ((imageStatus[modal.id] || 'loading') === 'loading') return;
              onSelect(modal);
            }}
          >
            <ThumbWrap>
              <Image 
                src={buildApiUrl('/api/uploads/' + (modal.thumb_path || modal.image_path))} 
                alt={modal.name} 
                ref={(img) => {
                  if (!img) return;
                  if (img.complete) {
                    setThumbStatus(modal.id, img.naturalWidth > 0 ? 'loaded' : 'error');
                  }
                }}
                onLoad={() => setThumbStatus(modal.id, 'loaded')}
                onError={() => setThumbStatus(modal.id, 'error')}
              />
              {(imageStatus[modal.id] || 'loading') === 'loading' && (
                <SpinnerOverlay>
                  <LocalSpinner $size={20} />
                </SpinnerOverlay>
              )}
            </ThumbWrap>
            <Name>{modal.name}</Name>
          </Card>
        ))}
      </Grid>

      {onClose && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <ActionButton onClick={onClose}>
            Cancel
          </ActionButton>
        </div>
      )}
    </div>
  );
};

export default EsqModals;
